/**
 * The anchor protocol (`ProductOverview.md` §4.4).
 *
 * One question, asked the moment both parties connect: "Fastest way to meet — one of you stays
 * put. Anchor or walk?" Choosing assigns the opposite role to the peer.
 *
 * It exists to head off the classic failure: two people converging simultaneously and orbiting
 * each other. Roles are advisory, swappable with one tap, and never enforced — the walker still
 * gets the full arrow if they ignore it, and the anchor can start walking whenever they like.
 */

import { compassPoint, formatDistance, type Role } from '@beeline/shared';

import { el } from '../../lib/dom';

export interface RoleQuestionOptions {
  peerName: string | null;
  onChoose: (role: Role) => void;
  onSkip: () => void;
}

/** The one question. Shown once, to whoever connected first. */
export function roleQuestion(options: RoleQuestionOptions): HTMLElement {
  const them = options.peerName ?? 'they';

  return el(
    'div',
    { class: 'roles' },
    el('h2', { class: 'roles__title', text: 'One of you stays put' }),
    el('p', {
      class: 'roles__body',
      text: `It is much faster than both walking — otherwise you will circle each other. ${
        options.peerName ? `${options.peerName} gets` : 'They get'
      } the opposite.`,
    }),
    el(
      'div',
      { class: 'roles__choices' },
      el(
        'button',
        {
          class: 'button button--primary button--block',
          type: 'button',
          onclick: () => options.onChoose('anchor'),
        },
        'I will stay here',
      ),
      el(
        'button',
        {
          class: 'button button--ghost button--block',
          type: 'button',
          onclick: () => options.onChoose('walker'),
        },
        `I will walk to ${them === 'they' ? 'them' : them}`,
      ),
    ),
    el(
      'button',
      { class: 'roles__skip', type: 'button', onclick: () => options.onSkip() },
      'We will work it out',
    ),
  );
}

export interface AnchorPanelOptions {
  peerName: string | null;
  onSwap: () => void;
}

export interface AnchorPanel {
  element: HTMLElement;
  update: (state: { distanceMetres: number | null; bearingFromPeer: number | null }) => void;
  destroy: () => void;
}

/**
 * The anchor's screen: calm, and doing nothing except counting down.
 *
 * No arrow, because an anchor has nowhere to go. What they get instead is the closing distance
 * and which way the other person is coming from, so they can look the right way.
 */
export function anchorPanel(options: AnchorPanelOptions): AnchorPanel {
  const who = options.peerName ?? 'They';
  const distance = el('output', { class: 'anchor__distance mono', text: '—' });
  const direction = el('p', { class: 'anchor__direction' });

  const element = el(
    'main',
    { class: 'screen anchor' },
    el(
      'div',
      { class: 'anchor__body' },
      el('h1', { class: 'anchor__title', text: 'Stay where you are' }),
      el('p', {
        class: 'anchor__lede',
        text: `${who} ${options.peerName ? 'is' : 'are'} coming to you.`,
      }),
      distance,
      direction,
    ),
    el(
      'footer',
      { class: 'anchor__foot' },
      el(
        'button',
        {
          class: 'button button--ghost button--block',
          type: 'button',
          onclick: () => options.onSwap(),
        },
        'Actually, I will walk',
      ),
    ),
  );

  return {
    element,
    update: ({ distanceMetres, bearingFromPeer }) => {
      distance.textContent = distanceMetres === null ? '—' : formatDistance(distanceMetres);
      direction.textContent =
        bearingFromPeer === null
          ? 'Waiting for their signal'
          : `Approaching from the ${compassPoint(bearingFromPeer)}`;
    },
    destroy: () => element.remove(),
  };
}
